import { memo, useState } from "react";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { MaterialInput } from "../../../common/components/materialInput/MaterialInput";
import { MarketingConsent } from "../../../MarketingConsent/components/MarketingConsent";
import { Controller, useForm } from "react-hook-form";
import { SimpleSelect } from "../../../common/components/Select/SimpleSelect";
import { ChangePasswordSection } from "../ChangePasswordSection";
import { type MyAccountData, type MyAccountValues } from "../../myAccountTypes";
import { HOMEPAGE } from "../../../utils/url/staticUrls";
import { MandatoryMessage } from "../common";
import { Row } from "../../../common/components/Layout";
import { isMarketingConsentEnabled } from "../../../utils/features";
import { makeSelectRef4RHF, useConfirmBeforeUnload } from "~/MyAccount/formUtils";
import { ZIPCODE_MAX_LENGTH, ZIPCODE_MIN_LENGTH, ZIPCODE_REGEXP } from "~/utils/validations";
import { editMyAccount } from "~/MyAccount/api/editMyAccount";
import { FormSection } from "~/MyAccount/MyAccountContainer";
import { TrainingConsent } from "~/MyAccount/components/TrainingConsent";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { useMyAccountDropdownValues } from "~/CompleteRegistration/UseMyAccountDropdownValues";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ModalConfirmation } from "~/MyAccount/components/ModalConfirmation";
import { MarketingCloudProfileCenterIframe } from "~/MyAccount/components/MarketingCloudProfileCenterIframe/MarketingCloudProfileCenterIframe";
import { MyAccountParts } from "~/MyAccount/components/MyAccountParts";
import { type EndCustomerDropdownOption } from "~/UserAccounts/EditUserAccountDTO";
import { setUserStatus } from "~/LMS/lmsUtils";
import trainingClasses from "../TrainingConsent.module.less";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import classes from "./MyAccountB2B.module.less";
import TrainingConsentInfo from "../TrainingConsentInfo/TrainingConsentInfo";
import { Alert } from "~/common/m3/Alert/Alert";
import { Snackbar, SnackbarCloseButton } from "~/common/m3/Snackbar/Snackbar";
import DeleteAccount from "../DeleteAccount/DeleteAccount";

interface Props {
  data: MyAccountData;
}

export interface DropdownOption {
  value: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label: string | any;
}

export interface AccountDropdownOption extends DropdownOption {
  endCustomerCodes: EndCustomerDropdownOption[];
}

const MyAccountB2B = (props: Props) => {
  const { data } = props;
  const { t } = useTranslation();
  const [newsletterOne, setNewsletterOne] = useState<boolean>(data.newsletterOne);
  const [isSubmitted, setSubmitted] = useState(data.newsletterOne);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [isEnableFieldsTraining] = useState<boolean>(data.inputFieldValues.lmsRequest);
  const dropdownOptions = useMyAccountDropdownValues(data);
  let haveInitialCategory = !!data.inputFieldValues.customerCategory;
  const { val: modalCategoryOpen, setTrue: openModalCategory, setFalse: closeModalCategory } = useBooleanState(false);

  const [showBEerror, setShowBEerror] = useState<boolean>(false);

  const { handleSubmit, control, trigger, watch, setError, formState, reset, getValues } = useForm<MyAccountValues>({
    defaultValues: data.inputFieldValues,
    reValidateMode: "onBlur",
  });

  const isMinDesktop = useIsMinDesktopSize();

  const onSubmit = (data: MyAccountValues, confirmed: boolean) => {
    if (!haveInitialCategory && !!customerCategoryCode && !confirmed && isEnableFieldsTraining) {
      return openModalCategory();
    } else {
      closeModalCategory();
    }
    setSubmitted(newsletterOne);
    editMyAccount({
      ...data,
      newsletterOne: newsletterOne,
      isEnableFieldsTraining: isEnableFieldsTraining,
    }).then((response) => {
      if (response.oldPasswordWrong) {
        setError(
          "oldPassword",
          {
            type: "wrongPassword",
          },
          { shouldFocus: true },
        );
      } else {
        if (response.success) {
          reset(getValues());
          setShowInfo(true);
          haveInitialCategory = true;
          setUserStatus(response.lmsState);
        } else {
          setShowBEerror(true);
        }
      }
    });
  };

  const getLabel = (name: string, required?: boolean) => t(`page.completeRegistration.${name}`) + (required ? "*" : "");
  const pleaseEnterError = (fieldName: string) => t("page.profile.pleaseEnter." + fieldName);

  const firstNameErrorMessage = pleaseEnterError("firstName").toLocaleLowerCase();
  const lastNameErrorMessage = pleaseEnterError("lastName").toLocaleLowerCase();
  const salutationErrorMessage = pleaseEnterError("salutation").toLocaleLowerCase();

  const hideMarketingConsent = isSubmitted && newsletterOne;

  const customerCategoryCode = watch("customerCategory");
  const customerCategoryLabel =
    dropdownOptions.customerCategoryOptions.find((item) => item.value === customerCategoryCode)?.stringLabel ?? "";

  useConfirmBeforeUnload(formState);

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data, false))} id={"complete-form"}>
      <PageTitle>{t("myAccount.title")}</PageTitle>
      <div className="scha__my-account">
        <Row className={"scha__my-account__form"}>
          <div className={"scha__my-account__form__column"}>
            <FormSection title={t("myAccount.name.title")}>
              <Controller
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <SimpleSelect
                    values={dropdownOptions.titleOptions}
                    label={t("profile.title")}
                    error={!!error}
                    fullWidth={true}
                    data-test-id={"my-account-title"}
                    onBlur={onBlur}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
                name={"title"}
                control={control}
                rules={{ required: false }}
              />

              <Controller
                render={({ field: { value, onChange, onBlur, ref }, fieldState: { invalid, error } }) => (
                  <SimpleSelect
                    values={dropdownOptions.salutationOptions}
                    label={t("page.completeRegistration.salutation") + "*"}
                    error={invalid}
                    fullWidth={true}
                    data-test-id={"my-account-salutation"}
                    onBlur={onBlur}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    ref={makeSelectRef4RHF(ref)}
                    helperText={error?.message ?? ""}
                  />
                )}
                name={"salutation"}
                control={control}
                rules={{
                  required: { value: true, message: salutationErrorMessage },
                }}
              />

              <Controller
                name="firstName"
                control={control}
                rules={{ required: { value: true, message: firstNameErrorMessage } }}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <MaterialInput
                    label={t("page.completeRegistration.firstName") + "*"}
                    colorHelperOnError={true}
                    data-test-id={"my-account-firstName"}
                    inputRef={ref}
                    error={!!error}
                    helperLabel={error?.message ?? true}
                    {...inputProps}
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: { value: true, message: lastNameErrorMessage },
                }}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <MaterialInput
                    label={t("page.completeRegistration.lastName") + "*"}
                    colorHelperOnError={true}
                    data-test-id={"my-account-lastName"}
                    error={!!error}
                    helperLabel={error?.message ?? true}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              <MyAccountParts.EmailParameter email={data.inputFieldValues.email} />
              <MyAccountParts.ChangeEmailLink href={data.profileCenterEmailLink} />
            </FormSection>
            <ChangePasswordSection
              className={classes.password}
              control={control}
              values={{
                oldPassword: watch("oldPassword"),
                newPassword: watch("newPassword"),
                newPasswordConfirmation: watch("newPasswordConfirmation"),
              }}
              triggerValidation={trigger}
            />
          </div>

          <div className={"scha__my-account__form__column"}>
            <FormSection title={t("myAccount.company.title")}>
              {isEnableFieldsTraining && (
                <Controller
                  name={"company"}
                  control={control}
                  rules={{ required: pleaseEnterError("companyName") }}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <MaterialInput
                      label={getLabel("companyName", true)}
                      data-test-id={"complete-registration-company"}
                      disabled={isEnableFieldsTraining}
                      colorHelperOnError={true}
                      error={!!error}
                      helperLabel={error?.message ?? true}
                      inputRef={ref}
                      {...inputProps}
                    />
                  )}
                />
              )}

              <Controller
                render={({ field: { value, onChange, onBlur } }) => (
                  <SimpleSelect
                    values={dropdownOptions.functionalAreaOptions}
                    label={getLabel("functionalArea")}
                    fullWidth={true}
                    data-test-id={"complete-registration-functional-area"}
                    onBlur={onBlur}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
                name={"functionalArea"}
                control={control}
              />
              <Controller
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <SimpleSelect
                    values={dropdownOptions.jobTitleOptions}
                    label={getLabel("jobTitle", false)}
                    fullWidth={true}
                    data-test-id={"complete-registration-job-title"}
                    onBlur={onBlur}
                    value={value}
                    error={!!error}
                    onChange={(e) => onChange(e.target.value)}
                    helperText=""
                  />
                )}
                name={"jobTitle"}
                control={control}
              />
              {isEnableFieldsTraining && (
                <Controller
                  name={"address"}
                  control={control}
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <MaterialInput
                      label={getLabel("address", isEnableFieldsTraining)}
                      data-test-id={"complete-registration-address"}
                      autoComplete={"address-line1"}
                      disabled={isEnableFieldsTraining}
                      inputRef={ref}
                      error={!!error}
                      helperLabel
                      {...inputProps}
                    />
                  )}
                />
              )}

              {isEnableFieldsTraining && (
                <div>
                  <div className="scha__my-account__form__inputs__zip-city">
                    <div className={"scha__my-account__form__inputs__zip-city__zip"}>
                      <Controller
                        name={"zipCode"}
                        control={control}
                        rules={{
                          minLength: {
                            value: ZIPCODE_MIN_LENGTH,
                            message: t("checkoutPage.addressModal.error.isTooShortPart", {
                              fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                            }),
                          },
                          maxLength: {
                            value: ZIPCODE_MAX_LENGTH,
                            message: t("checkoutPage.addressModal.error.isTooLongPart", {
                              fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                            }),
                          },
                          pattern: {
                            value: ZIPCODE_REGEXP,
                            message: t("checkoutPage.addressModal.error.forbidenCharactersPart", {
                              fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                            }),
                          },
                          required: isEnableFieldsTraining,
                        }}
                        render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                          <MaterialInput
                            label={getLabel("zipCode", isEnableFieldsTraining)}
                            data-test-id={"complete-registration-zipCode"}
                            error={!!error}
                            helperLabel={error?.message}
                            disabled={isEnableFieldsTraining}
                            onBeforeInput={(e) => {
                              // @ts-ignore
                              const val = e.target.value + e.data;
                              if (!ZIPCODE_REGEXP.test(val.trim())) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                            autoComplete={"postal-code"}
                            inputRef={ref}
                            {...inputProps}
                          />
                        )}
                      />
                    </div>

                    <div className="scha__my-account__form__inputs__zip-city__city">
                      <Controller
                        rules={{
                          required: isEnableFieldsTraining,
                        }}
                        name={"city"}
                        control={control}
                        render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                          <MaterialInput
                            label={getLabel("city", isEnableFieldsTraining)}
                            data-test-id={"complete-registration-city"}
                            autoComplete={"city"}
                            error={!!error}
                            disabled={isEnableFieldsTraining}
                            inputRef={ref}
                            {...inputProps}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {isEnableFieldsTraining && (
                <Controller
                  name={"country"}
                  control={control}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleAutocomplete
                      label={getLabel("country", true)}
                      data-test-id={"complete-registration-country"}
                      values={dropdownOptions.countryOptions}
                      fullWidth={true}
                      error={!!error}
                      helperText={error?.message ?? " "}
                      onBlur={onBlur}
                      value={value}
                      onChange={onChange}
                      disabled={isEnableFieldsTraining}
                    />
                  )}
                  rules={{ required: isEnableFieldsTraining }}
                />
              )}

              {isEnableFieldsTraining && (
                <Controller
                  name={"vat"}
                  control={control}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <MaterialInput
                      label={getLabel("vat")}
                      helperLabel={getLabel("exampleVat")}
                      data-test-id={"complete-registration-vat"}
                      error={!!error}
                      inputRef={ref}
                      {...inputProps}
                    />
                  )}
                />
              )}
            </FormSection>
          </div>

          <div className={"scha__my-account__form__column"}>
            {isMinDesktop && (
              <div className="d-flex">
                <h3 className={"scha__my-account-form__section__title mr-8"}>
                  {t("page.registerForFree.baner.trainingCampus")}
                </h3>
                <div className={trainingClasses.flag}>{t("myAccount.training.flag.label")}</div>
              </div>
            )}
            <TrainingConsent
              className={"scha__my-account__training-campus"}
              checked={isEnableFieldsTraining}
              disabled={true}
            />
            {isEnableFieldsTraining && <p className="my-30 title-medium">{t("myAccount.training.welcome")}</p>}
            {isEnableFieldsTraining && (
              <div className="mb-16">
                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={dropdownOptions.languageOptions}
                      label={getLabel("language", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-language"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"language"}
                  control={control}
                />
              </div>
            )}
            {isEnableFieldsTraining && (
              <div className="mb-16">
                {!haveInitialCategory && (
                  <Alert severity={"info"} className={"mb-2"}>
                    {t("myAccount.customerCategory.info.text")}
                  </Alert>
                )}
                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={dropdownOptions.customerCategoryOptions}
                      label={getLabel("customerCategory", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-customer-category"}
                      disabled={haveInitialCategory}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"customerCategory"}
                  control={control}
                />
              </div>
            )}
            {isEnableFieldsTraining && (
              <div className="mb-16">
                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={dropdownOptions.knowledgeAreaOptions}
                      label={getLabel("knowledgeArea", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-knowledge-area"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"knowledgeArea"}
                  control={control}
                />
              </div>
            )}
          </div>
        </Row>

        <MandatoryMessage />

        {isMarketingConsentEnabled() && !hideMarketingConsent && (
          <MarketingConsent selected={newsletterOne} setSelected={(newVal: boolean) => setNewsletterOne(newVal)} />
        )}

        {/* BUTTONS*/}
        <div className="scha__my-account__form__buttons-area">
          <Button variant={"text"} href={HOMEPAGE} data-test-id={"my-account-button-cancel"}>
            {t("page.completeRegistration.cancel")}
          </Button>
          <Button data-test-id={"my-account-button-submit"} onClick={handleSubmit((data) => onSubmit(data, false))}>
            {t("text.company.save.button")}
          </Button>
        </div>

        {isMarketingConsentEnabled() && hideMarketingConsent && (
          <MarketingCloudProfileCenterIframe
            className={"scha__my-account__mcProfile-iframe"}
            url={data.profileCenterLink}
          />
        )}

        <DeleteAccount />

        <Snackbar
          open={showInfo}
          onClose={() => setShowInfo(false)}
          message={t("myAccount.successInfo")}
          action={<SnackbarCloseButton onClick={() => setShowInfo(false)} />}
        />
        <Snackbar
          open={showBEerror}
          onClose={() => setShowBEerror(false)}
          message={t("myAccount.dataLoadError")}
          action={<SnackbarCloseButton onClick={() => setShowBEerror(false)} />}
        />
      </div>

      {modalCategoryOpen && (
        <ModalConfirmation
          onClose={closeModalCategory}
          onSubmit={handleSubmit((data) => onSubmit(data, true))}
          selectedValue={customerCategoryLabel}
          inputField={t(`page.completeRegistration.customerCategory`)}
        />
      )}
    </form>
  );
};

export default memo(MyAccountB2B);
